import { FabricEditor } from 'modules/fabric-editor';
import { fetchTemplateOnly, resetEditor } from 'modules/manager/store';
import { useEffect, useRef, useState } from 'react';
import { useAppDispatch } from 'store/hooks';
import type { RenderedImageLink, RenderStatus } from './render-shared';

interface UseRenderPageStateOptions {
  /** `?templateId=N`; `null` means the URL did not carry a usable one. */
  templateId: number | null;
  /**
   * Error text for the route's own required parameter (`matchId`, `stageId`,
   * `championshipId`), or `null` while that parameter is present.
   */
  missingParamMessage: string | null;
  /** `error` from the route's render-context query, if it failed. */
  contextError?: unknown;
  /** Fallback text when the failed context query carries no message. */
  contextErrorFallback: string;
}

/**
 * The bookkeeping every headless render page repeats: fetch the template,
 * reset the editor on unmount, report missing URL parameters and a failed
 * render-context query, and hold the editor/status/links state the page's own
 * orchestration effect writes into.
 */
export function useRenderPageState(options: UseRenderPageStateOptions) {
  const { templateId, missingParamMessage, contextError, contextErrorFallback } = options;
  const dispatch = useAppDispatch();

  const [editor, setEditor] = useState<FabricEditor | null>(null);
  const [status, setStatus] = useState<RenderStatus>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [links, setLinks] = useState<RenderedImageLink[]>([]);
  const orchestrated = useRef(false);

  useEffect(() => {
    if (templateId === null) {
      setStatus('error');
      setErrorMessage('Missing templateId in URL');
      return;
    }
    dispatch(fetchTemplateOnly({ templateId }));
    return () => {
      dispatch(resetEditor());
    };
  }, [dispatch, templateId]);

  useEffect(() => {
    if (missingParamMessage === null) return;
    setStatus('error');
    setErrorMessage(missingParamMessage);
  }, [missingParamMessage]);

  useEffect(() => {
    if (!contextError) return;
    console.error(contextError);
    setStatus('error');
    setErrorMessage((contextError as { message?: string }).message ?? contextErrorFallback);
  }, [contextError, contextErrorFallback]);

  return { editor, setEditor, status, setStatus, errorMessage, setErrorMessage, links, setLinks, orchestrated };
}
